import { useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import { createPatientSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreatePatientMutation } from '@common/cache';
import { GENDER_TYPE, paths } from '@common/types';
import { t } from 'i18next';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';

import { PatientCreatedToast } from '../components';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { theme } from '@store';
import { InferType } from 'yup';

type ICreatePatientFormShape = InferType<typeof createPatientSchema>;

const initialValues: ICreatePatientFormShape = {
  first_name: '',
  middle_name: '',
  last_name: '',
  gender: GENDER_TYPE.MALE,
  birthdate: '',
  practice_id: '',
  provider_type: 'Internal',
  provider_id: '',
  provider_first_name: '',
  provider_last_name: '',
  provider_phone: '',
  lives_at_home: 'true',
  street: '',
  suite: '',
  room: '',
  zip_code: '',
  city: '',
  state: '',
  county: '',
  building_id: '',
  building_unit: '',
  google_places_id: '',
};

export const useCreatePatient = () => {
  const formMethods = useForm<ICreatePatientFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreatePatientFormShape, any, any>(
      createPatientSchema,
    ),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
  } = formMethods;

  const navigate = useNavigate();

  const createPatientMutation = useCreatePatientMutation();
  const [isOpen, setIsOpen] = useState(false);

  const genderOptions = [
    { value: GENDER_TYPE.MALE, label: t('patients.addModal.gender.male') },
    { value: GENDER_TYPE.FEMALE, label: t('patients.addModal.gender.female') },
    {
      value: GENDER_TYPE.UNKNOWN,
      label: t('patients.addModal.gender.unknown'),
    },
  ];

  const buildingUnitOptions = [
    {
      value: 'assisted_living',
      label: t('patients.addModal.building_unit.assisted_living'),
    },
    {
      value: 'memory_care',
      label: t('patients.addModal.building_unit.memory_care'),
    },
    {
      value: 'senior_living',
      label: t('patients.addModal.building_unit.senior_living'),
    },
    {
      value: 'independent_living',
      label: t('patients.addModal.building_unit.independent_living'),
    },
  ];

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: ICreatePatientFormShape) => {
    try {
      const response = await createPatientMutation.mutateAsync({
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
        preferred_name: values.preferred_name,
        has_preferred_name: !!values.preferred_name,
        gender: values.gender,
        birthdate: new Date(values.birthdate).toISOString(),
        provider_type: values.provider_type,
        practice_id: values.practice_id,
        provider_id: values.provider_id || undefined,
        provider_first_name: values.provider_first_name,
        provider_last_name: values.provider_last_name,
        provider_phone: values.provider_phone,
        lives_at_home: values.lives_at_home === 'true',
        ...(values.lives_at_home === 'false' && {
          building_id: values.building_id,
        }),
        ...(values.lives_at_home === 'false' && {
          building_unit:
            values.building_unit as paths['/patient']['post']['requestBody']['content']['application/json']['building_unit'],
        }),
        ...(values.lives_at_home === 'false' && { room: values.room }),

        ...(values.lives_at_home === 'true' && {
          google_places_id: values.google_places_id,
        }),
        ...(values.lives_at_home === 'true' && { street: values.street }),
        ...(values.lives_at_home === 'true' && { suite: values.suite }),
        ...(values.lives_at_home === 'true' && { city: values.city }),
        ...(values.lives_at_home === 'true' && { state: values.state }),
        ...(values.lives_at_home === 'true' && { zip_code: values.zip_code }),
        ...(values.lives_at_home === 'true' && { county: values.county }),
      });

      if (response && response.data && response.data.id) {
        const handleGoToPatient = () => {
          navigate(`/patient/${response.data.id}`);
        };

        toast(
          props => (
            <Box
              sx={{
                display: 'flex',
                padding: 0,
                margin: 0,
                borderRadius: 0,
                width: '500px',
                justifyContent: 'center',
                px: '0.5rem',
              }}
            >
              <PatientCreatedToast goToPatient={handleGoToPatient} {...props} />
            </Box>
          ),
          {
            autoClose: 5000,
            style: {
              backgroundColor: theme.palette.success.main,
              width: '500px',
              padding: 0,
              margin: 0,
              borderRadius: 0,
              boxShadow: 'none',
            },
            closeOnClick: false,
            closeButton: false,
            position: 'bottom-left',
            hideProgressBar: true,
          },
        );
      }

      handleClose();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  return {
    formMethods,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    genderOptions,
    buildingUnitOptions,
    handleSelectLocation,
  };
};
